import { useState, useRef, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Title } from "../title"

import { ArrowLeftIcon, PencilSimpleIcon } from '@phosphor-icons/react'

import styles from './header-page.module.css'

import type { HeaderPageProps } from './header-page.type'
import { Skeleton } from "../skeleton"

export const HeaderPage = ({ title, onTitleChange, isLoading, backNavigationLink, withBackButton }: HeaderPageProps) => {
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(title);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setEditValue(title);
    }, [title]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    const handleSave = () => {
        if (editValue.trim() !== '' && editValue !== title) {
            onTitleChange?.(editValue);
        } else {
            setEditValue(title);
        }
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            setEditValue(title);
            setIsEditing(false);
        }
    };

    if (isLoading) {
        return (
            <div className={styles.container}>

                {withBackButton && <Skeleton className={styles['back-button']} />}

                <Title variant='h5' as='h1' isLoading />
            </div>
        )
    }

    const renderBackButton = () => {
        if (!withBackButton) return null;

        if (backNavigationLink) {
            return (
                <Link to={backNavigationLink}>
                    <ArrowLeftIcon className={styles['back-button-icon']} />
                </Link>
            );
        }

        return (
            <button
                type="button"
                onClick={() => navigate(-1)}
                className={styles['back-button-action']}
            >
                <ArrowLeftIcon className={styles['back-button-icon']} />
            </button>
        );
    };

    return (
        <div className={styles.container}>
            {renderBackButton()}

            {isEditing && onTitleChange ? (
                <input
                    ref={inputRef}
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    className={styles['title-input']}
                />
            ) : (
                <div
                    className={styles['title-container']}
                    onDoubleClick={() => setIsEditing(true)}
                >
                    <Title variant='h5' as='h1'>{title}</Title>

                    {onTitleChange && (
                        <PencilSimpleIcon
                            className={styles['pencil-icon']}
                            size={20}
                            weight="bold"
                            onClick={() => setIsEditing(true)}
                        />
                    )}
                </div>
            )}
        </div >
    )
}